<script setup>
import { computed, ref, shallowRef, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'

// RED-317 §8 (PR 4b) "Laadi Wordist": pick a .docx (a whole mall, or the
// overview's text edited in Word), then the part to take — from a heading
// (or the document start) to the next heading of its level, a later heading
// or bold line, or the document end. The server cuts the range with its
// charts, tables and pictures and replaces the draft with it. A whole
// county mall comes with its §4.1/§4.2 located the way the generation job
// cuts them; this cell's section is preselected.
const props = defineProps({
  // The parent's upload/apply: they return the outline / true, or null / false with `errors` set.
  upload: { type: Function, required: true },
  apply: { type: Function, required: true },
  errors: { type: Object, required: true },
  busy: { type: String, default: null },
  market: { type: Boolean, default: true },
})

const emit = defineEmits(['done', 'cancel'])

const { t } = useI18n()

const input = useTemplateRef('input')
// PrimeVue's Select shows a null value as nothing chosen, so the document's
// start and end are named options, sent as null (the server's default).
const FROM_START = 'start'
const TO_END = 'end'

const outline = shallowRef(null)
const start = ref(FROM_START)
const end = ref('auto')
const headingAsTitle = ref(props.market)

const pick = () => input.value?.click()

/** The mall's range for this cell's section ({ start, end }), or null for any other file. */
const suggested = computed(() => outline.value?.sections?.[props.market ? 'market' : 'economy'] ?? null)

const onFile = async event => {
  const file = event.target.files?.[0]
  event.target.value = ''
  if (!file) {
    return
  }
  const result = await props.upload(file)
  if (result) {
    outline.value = result
    const section = suggested.value
    start.value = section?.start ?? FROM_START
    end.value = section?.end ?? TO_END
  }
}

const headings = computed(() => (outline.value?.marks ?? []).filter(mark => mark.kind === 'heading'))

const startOptions = computed(() => [
  { value: FROM_START, label: t('messages.admin.market_overviews.import.from_start') },
  ...headings.value.map(mark => ({ value: mark.index, label: `${'  '.repeat(Math.max(0, mark.level - 1))}${mark.text}` })),
])

const startMark = computed(() => headings.value.find(mark => mark.index === start.value) ?? null)

const endOptions = computed(() => {
  const later = (outline.value?.marks ?? []).filter(mark => start.value === FROM_START || mark.index > start.value)
  return [
    ...(startMark.value ? [{ value: 'auto', label: t('messages.admin.market_overviews.import.to_auto') }] : []),
    ...later.map(mark => ({ value: mark.index, label: t('messages.admin.market_overviews.import.before', { text: mark.text }) })),
    { value: TO_END, label: t('messages.admin.market_overviews.import.to_end') },
  ]
})

const onStart = value => {
  start.value = value
  if (value === FROM_START) {
    end.value = TO_END
  } else {
    end.value = value === suggested.value?.start ? suggested.value.end : 'auto'
  }
}

const submit = async () => {
  const done = await props.apply(outline.value.token, {
    start: start.value === FROM_START ? null : start.value,
    end: end.value === 'auto' || end.value === TO_END ? null : end.value,
    headingAsTitle: props.market && headingAsTitle.value && startMark.value !== null,
  })
  if (done) {
    outline.value = null
    emit('done')
  }
}

const cancel = () => {
  outline.value = null
  emit('cancel')
}

defineExpose({ pick })
</script>

<template>
  <div class="rounded-lg border border-gray-200 bg-white p-4" data-testid="mo-import">
    <input ref="input" type="file" accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document" class="hidden" data-testid="mo-import-input" @change="onFile" />

    <div v-if="!outline" class="flex flex-wrap items-center gap-3">
      <Button
        type="button"
        size="small"
        outlined
        icon="pi pi-upload"
        :label="busy === 'import' ? $t('messages.admin.market_overviews.import.uploading') : $t('messages.admin.market_overviews.import.choose_file')"
        :loading="busy === 'import'"
        data-testid="mo-import-choose"
        @click="pick"
      />
      <Button type="button" size="small" text severity="secondary" :label="$t('messages.admin.market_overviews.editor.cancel')" @click="cancel" />
      <p class="basis-full text-xs text-gray-500">{{ $t('messages.admin.market_overviews.import.hint') }}</p>
    </div>

    <div v-else class="flex flex-col gap-3">
      <p class="text-sm text-gray-700" data-testid="mo-import-file">
        <i class="pi pi-file-word mr-1 text-primary-600" />{{ $t('messages.admin.market_overviews.import.file', { name: outline.file }) }}
      </p>
      <p v-if="suggested" class="text-xs text-gray-500" data-testid="mo-import-mall">{{ $t('messages.admin.market_overviews.import.mall_detected') }}</p>
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div class="flex flex-col gap-1">
          <label for="mo-import-from" class="text-xs font-medium text-gray-700">{{ $t('messages.admin.market_overviews.import.from') }}</label>
          <Select
            input-id="mo-import-from"
            :model-value="start"
            :options="startOptions"
            option-label="label"
            option-value="value"
            fluid
            data-testid="mo-import-from"
            @update:model-value="onStart"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label for="mo-import-to" class="text-xs font-medium text-gray-700">{{ $t('messages.admin.market_overviews.import.to') }}</label>
          <Select
            v-model="end"
            input-id="mo-import-to"
            :options="endOptions"
            option-label="label"
            option-value="value"
            fluid
            data-testid="mo-import-to"
          />
        </div>
      </div>
      <label v-if="market && startMark" class="flex items-center gap-2 text-sm text-gray-700">
        <Checkbox v-model="headingAsTitle" binary input-id="mo-import-title" data-testid="mo-import-heading-title" />
        {{ $t('messages.admin.market_overviews.import.heading_as_title') }}
      </label>
      <p class="text-sm text-red-600" :class="{ invisible: !errors.range && !errors.file }" data-testid="mo-import-error">{{ errors.range ?? errors.file ?? '·' }}</p>
      <div class="flex justify-end gap-2">
        <Button type="button" size="small" text severity="secondary" :label="$t('messages.admin.market_overviews.editor.cancel')" @click="cancel" />
        <Button
          type="button"
          size="small"
          icon="pi pi-download"
          :label="$t('messages.admin.market_overviews.import.apply')"
          :loading="busy === 'apply'"
          data-testid="mo-import-apply"
          @click="submit"
        />
      </div>
    </div>

    <p v-if="!outline && errors.file" class="mt-2 text-sm text-red-600" data-testid="mo-import-file-error">{{ errors.file }}</p>
  </div>
</template>
